import React, { useEffect, useState, useCallback, useRef } from 'react';
import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Box, Typography, Card, CardContent, Container, Divider } from '@mui/material';
import { Helmet } from 'react-helmet';
import { RootState } from '../store/store';
import AppConstants from '../constants/AppConstants';
import GoogleSheetsService from '../services/GoogleSheetsService';
import { GoogleSheetTemplate } from '../store/actions/googleSheetsActions';

interface Props {
  templates: GoogleSheetTemplate[];
}

const GoogleSheetViewer: React.FC<Props> = ({ templates }) => {
  const { templateId } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [iframeSrc, setIframeSrc] = useState<string | undefined>(undefined);
  const [iframeHtml, setIframeHtml] = useState<string | undefined>(undefined);
  const [template, setTemplate] = useState<GoogleSheetTemplate | null>(null);
  const previousBlobUrlRef = useRef<string | null>(null);

  const handleIframeLoad = () => setIsLoading(false);
  const handleIframeError = () => { setIsLoading(false); setHasError(true); };

  const loadTemplate = useCallback(async (tmpl: GoogleSheetTemplate) => {
    if (!tmpl || !tmpl.googleSheetId) return;
    setIsLoading(true);
    setHasError(false);

    try {
      const fileResponse: any = await GoogleSheetsService.downloadGoogleSheetHtmlTemplate(
        tmpl.id,
        tmpl.googleSheetId
      );

      if (fileResponse?.data instanceof Blob) {
        if (previousBlobUrlRef.current) {
          URL.revokeObjectURL(previousBlobUrlRef.current);
        }

        // Attempt to read blob as text (HTML)
        const text = await fileResponse.data.text().catch(() => null);
        if (text) {
          setIframeHtml(text);
          setIframeSrc(undefined);
        } else {
          const blobUrl = URL.createObjectURL(fileResponse.data);
          previousBlobUrlRef.current = blobUrl;
          setIframeSrc(blobUrl);
          setIframeHtml(undefined);
        }
      } else {
        setHasError(true);
      }
    } catch (err) {
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (templateId && templates.length > 0) {
      const foundTemplate = templates.find((t) => t.id === parseInt(templateId, 10));
      if (foundTemplate) {
        setTemplate(foundTemplate);
        loadTemplate(foundTemplate);
      } else {
        setHasError(true);
        setIsLoading(false);
      }
    }
  }, [templateId, templates, loadTemplate]);

  // Cleanup blob URL on unmount
  useEffect(() => () => {
    if (previousBlobUrlRef.current) URL.revokeObjectURL(previousBlobUrlRef.current);
  }, []);

  return (
    <>
      <Helmet>
        <title>{template?.tabName || 'Google Sheet'} | {AppConstants.APP_NAME}</title>
      </Helmet>
      <Box
        sx={{
          backgroundColor: 'background.default',
          minHeight: '100%',
          py: 3
        }}
      >
        <Container maxWidth={false}>
          <Card sx={{ height: 'calc(100vh - 120px)' }}>
            <Box sx={{ p: 2 }}>
              <Typography variant="h3">
                {template?.tabName || 'Google Sheet'}
              </Typography>
            </Box>
            <Divider />
            <CardContent sx={{ padding: 0, height: 'calc(100% - 64px)', position: 'relative' }}>
              {isLoading && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 1
                  }}
                >
                  <Typography variant="body1">Loading...</Typography>
                </Box>
              )}

              {hasError && !isLoading && (
                <Box sx={{ p: 2 }}>
                  <Typography variant="body2" color="error">
                    Failed to load template. Please try again later.
                  </Typography>
                </Box>
              )}

              {!hasError && (iframeSrc || iframeHtml) && (
                <iframe
                  src={iframeSrc}
                  srcDoc={iframeHtml}
                  title={template?.tabName || 'Google Sheet'}
                  width="100%"
                  height="100%"
                  frameBorder="0"
                  style={{ border: 'none', display: 'block' }}
                  onLoad={handleIframeLoad}
                  onError={handleIframeError}
                />
              )}
            </CardContent>
          </Card>
        </Container>
      </Box>
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  templates: state.googleSheets?.templates || []
});

const mapDispatchToProps = (dispatch: any) => ({});

export default connect(mapStateToProps, mapDispatchToProps)(GoogleSheetViewer);
